/**
 * Live transcript for one session, read through a window (RFC 0317 §3c).
 *
 * A session is single-doc: the whole conversation lives inline under the
 * session doc's `thread` key, so there are no pages to fetch. What the window
 * bounds is what the pane MOUNTS: the refactor session, 1,300 entries, took
 * two minutes to paint on the laptop it ran on, and the window could not
 * respond until it had. The projection is cheap (it is cached per document
 * version in mod-core); the rows are not. So the newest `WINDOW` messages
 * render first, and "load earlier" widens by another window. The window never
 * shrinks while the pane is open, and it resets when the session changes.
 *
 * The cut lands on a turn boundary where one is near: a window that opened in
 * the middle of an assistant turn would show tool results with no call.
 */

import { type AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { evictDocuments } from '@mod/core/core'
import {
  projectSessionTranscript,
  type TranscriptMessage,
} from '@mod/core/model'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { holdDocument } from '../../services/world/repo/held-docs'

/** Messages a pane mounts with, and grows by. */
export const TRANSCRIPT_WINDOW = 120

/**
 * The newest `limit` messages, opened out to the start of the turn the cut
 * lands in, so long as that costs less than another window.
 */
export function windowTranscript(
  transcript: readonly TranscriptMessage[],
  limit: number,
): { messages: TranscriptMessage[]; hasMore: boolean } {
  if (transcript.length <= limit) {
    return { messages: [...transcript], hasMore: false }
  }
  let start = transcript.length - limit
  const floor = Math.max(0, start - limit)
  for (let i = start; i >= floor; i--) {
    if (transcript[i]?.role === 'user') {
      start = i
      break
    }
  }
  return { messages: transcript.slice(start), hasMore: start > 0 }
}

/**
 * Earlier pages' messages ahead of the live page's (RFC 0360 S4). The live
 * page carries the closed page's last messages as its tail, so those are
 * dropped from the earlier side by entry id rather than shown twice.
 */
export function joinPages(
  earlier: readonly TranscriptMessage[],
  live: readonly TranscriptMessage[],
): TranscriptMessage[] {
  const onLive = new Set<string>()
  for (const m of live) if (m.entryId) onLive.add(String(m.entryId))
  return [
    ...earlier.filter((m) => !m.entryId || !onLive.has(String(m.entryId))),
    ...live,
  ]
}

/** A closed page the pane has opened to scroll back (RFC 0360 S4). */
interface OpenedPage {
  id: string
  transcript: TranscriptMessage[]
  /** The page before it, or null on the session's first page. */
  previousPage: string | null
}

function previousPageOf(doc: unknown): string | null {
  const v = (doc as { previousPage?: unknown } | null)?.previousPage
  const id = v === undefined || v === null ? '' : String(v)
  return id.length > 0 ? id : null
}

export function useSessionTranscript(
  _repo: Repo,
  sessionId: string,
  sessionDoc: unknown,
): {
  transcript: TranscriptMessage[]
  /** The complete projected log. Keep it out of the DOM, but use it for cheap
   *  folds such as the exact changed-items count in the session header. */
  fullTranscript: TranscriptMessage[]
  /** The live page's own messages, no earlier page joined: what a fold that
   *  starts from the page seed adds to (RFC 0360 S2). */
  liveTranscript: TranscriptMessage[]
  hasMore: boolean
  loadMore: () => void
  loading: boolean
} {
  const live = useMemo(() => projectSessionTranscript(sessionDoc), [sessionDoc])
  const [limit, setLimit] = useState(TRANSCRIPT_WINDOW)
  // Closed pages opened so far, oldest first (RFC 0360 S4). A page is read
  // once and held until the session changes or the pane goes; then let go.
  const [pages, setPages] = useState<OpenedPage[]>([])
  const [loading, setLoading] = useState(false)
  const opened = useRef<string[]>([])
  /** Releases for the pages held open, one per page (RFC 0317 3d). */
  const holds = useRef<(() => void)[]>([])
  // biome-ignore lint/correctness/useExhaustiveDependencies: the window resets when the session changes, and only then
  useEffect(() => {
    setLimit(TRANSCRIPT_WINDOW)
    setPages([])
    return () => {
      const ids = opened.current
      opened.current = []
      for (const release of holds.current) release()
      holds.current = []
      if (ids.length > 0) void evictDocuments(_repo, ids).catch(() => {})
    }
  }, [sessionId])
  const whole = useMemo(
    () =>
      pages.length === 0
        ? live
        : joinPages(
            pages.flatMap((p) => p.transcript),
            live,
          ),
    [pages, live],
  )
  const { messages, hasMore: moreInHand } = useMemo(
    () => windowTranscript(whole, limit),
    [whole, limit],
  )
  // The page before the oldest one in hand, if there is one.
  const earlierPage = pages[0]
    ? pages[0].previousPage
    : previousPageOf(sessionDoc)
  const hasMore = moreInHand || earlierPage !== null
  const loadMore = useCallback(() => {
    if (moreInHand) {
      setLimit((n) => n + TRANSCRIPT_WINDOW)
      return
    }
    if (earlierPage === null || loading) return
    setLoading(true)
    void (async () => {
      try {
        // The stock findWithProgress: the Mod find follows `movedTo`, which
        // would land a closed page's id back on the live page (and the
        // stock find goes through this repo's wrapped findWithProgress).
        const handle = await Repo.prototype.findWithProgress
          .call(_repo, earlierPage as AnyDocumentId)
          .whenReady()
        const doc = handle.doc() as unknown
        const page: OpenedPage = {
          id: earlierPage,
          transcript: projectSessionTranscript(doc),
          previousPage: previousPageOf(doc),
        }
        opened.current.push(earlierPage)
        // Held while shown, so idle eviction cannot take it from the pane.
        holds.current.push(holdDocument(_repo, earlierPage))
        setPages((p) => (p[0]?.id === page.id ? p : [page, ...p]))
        setLimit((n) => n + TRANSCRIPT_WINDOW)
      } finally {
        setLoading(false)
      }
    })()
  }, [_repo, moreInHand, earlierPage, loading])
  return {
    transcript: messages,
    fullTranscript: whole,
    liveTranscript: live,
    hasMore,
    loadMore,
    loading,
  }
}
